import { Filter } from '@aionzdev/design-system';
import { hideGlobalDialogErrorContext, showGlobalDialogErrorContext } from '@aionzdev/modules';
import { PurchaseRequestStatus, UiCapability } from '../enums/index';
import { AddSupplierToProductDto } from '../interfaces/add-supplier-to-product.dto';
import { ApproveQuotationMap } from '../interfaces/approve-quotation-map';
import { BuyerItem } from '../interfaces/buyer-item.interface';
import { CustomerTradingFocItem } from '../interfaces/customer-trading-foc-dialog-data';
import { DuplicatedProductResponseDto } from '../interfaces/duplicated-product-response.dto';
import { FindAllPurchasesRequestDto } from '../interfaces/find-purchase-request.dto';
import { MergePurchaseRequestItemsDto } from '../interfaces/merge-purchase-request-items.dto';
import { ProductIntegrationDialogData } from '../interfaces/product-integration-dialog-data';
import { PurchaseItemStatus } from '../interfaces/purchase-request';
import { PurchaseRequestAttachmentGroup } from '../interfaces/purchase-request-attachment-group';
import { PurchaseRequestAuditDto, PurchaseRequestAuditRequestDto } from '../interfaces/purchase-request-audit.dto';
import { PurchaseRequestBranch } from '../interfaces/purchase-request-branch';
import { PurchaseRequestCommentDto } from '../interfaces/purchase-request-comment.dto';
import { PurchaseRequestDocumentUploadItemResult } from '../interfaces/purchase-request-document-upload-item-result';
import { PurchaseRequestResponseDto } from '../interfaces/purchase-response.dto';
import { PurchaseSupplierCreate } from '../interfaces/purchase-supplier';
import { PurchasingEvent } from '../interfaces/purchasing-event';
import { QuotationMapData } from '../interfaces/quotation-map-data';
import { QuotationMapDraft } from '../interfaces/quotation-map-draft';
import { RealtimeConfig } from '../interfaces/realtime-config';
import { SendPurchaseRequestForQuoteItem } from '../interfaces/send-purchase-request-for-quote-dialog-data';
import { SupplierInfo } from '../interfaces/supplier-info';
import { TimePerStageMetrics } from '../interfaces/time-per-stage-metrics';
import { TradingMatchSuggestion } from '../interfaces/trading-match-suggestion';
import { TriggerSupplierSuggestionSearchResult } from '../interfaces/trigger-supplier-suggestion-search-result';
import { UpdatePurchaseItemInfoDto } from '../interfaces/update-purchase-item-info.dto';
import { UpdatePurchaseOrderItemDto } from '../interfaces/update-purchase-order-status.dto';
import { UpdatePurchaseRequestStatusDto } from '../interfaces/update-purchase-request-status.dto';
import { UpdateSuggestedSupplierStatusDto } from '../interfaces/update-suggested-supplier-status.dto';
import { UpdateTradingMatchSuggestionStatusDto } from '../interfaces/update-trading-match-suggestion-status.dto';
import { UploadOfferDocumentsDto } from '../interfaces/upload-offer-documents.dto';
import { AbstractPurchasingService } from './abstract-purchasing.service';
import { SelectorListResponse } from '../shared/selector-list/interfaces/selector-list-response.interface';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { computed, Injectable, inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { NavigationCancel, NavigationCancellationCode, NavigationEnd, NavigationError, NavigationSkipped, Router } from '@angular/router';
import { Observable, forkJoin, of, throwError } from 'rxjs';
import { catchError, filter, map, switchMap, take, tap } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { PapelEmpresa } from '../../../core/models/papel-empresa.enum';
import { EnderecoApiService } from '../../../core/services/api/endereco-api.service';
import { Endereco } from '../../../core/models/endereco';
import { FormTempApiService } from '../../../core/services/api/form-temp-api.service';
import { AuthModuleService } from '../../../core/services/auth-module.service';
import { CompanyContextService } from '../../../core/services/company-context.service';
import { TenantContextService } from '../../../core/services/tenant-context.service';
import { AcceptOffersRequestDto } from '../interfaces/dtos/accept-offers-request.dto';
import { BuyerResponseDto } from '../interfaces/dtos/buyer-response.dto';
import { CheckDuplicatedProductsResponseDto } from '../interfaces/dtos/check-duplicates-response.dto';
import { CustomerTradingFocResponseDto } from '../interfaces/dtos/customer-trading-foc-response.dto';
import { DashboardMetricsResponseDto } from '../interfaces/dtos/dashboard-metrics-response.dto';
import { FindAllPurchaseRequestOffersResponseDto } from '../interfaces/dtos/find-all-purchase-offers-response.dto';
import { FindAllPurchaseProductsResponseDto } from '../interfaces/dtos/find-all-purchase-products-response.dto';
import { FindAllPurchaseOffersResponseDto } from '../interfaces/dtos/find-all-purchase-response.dto';
import { FIndOfferResponseDto } from '../interfaces/dtos/find-offer-response.dto';
import { UncompletedPurchaseResponseDto } from '../interfaces/dtos/find-uncompleted-purchases-response.dto';
import { PurchaseRequestAuditResponseDto } from '../interfaces/dtos/purchase-request-audit-response.dto';
import { PurchaseRequestCommentResponseDto } from '../interfaces/dtos/purchase-request-comment-response.dto';
import { RetratoAtualResponseDto } from '../interfaces/dtos/retrato-atual-response.dto';
import { StageTimeMetricsResponseDto } from '../interfaces/dtos/stage-time-metrics-response.dto';
import { SupplierInfoResponseDto } from '../interfaces/dtos/supplier-info-response.dto';
import { QuotationSupplierCacheEntry } from '../interfaces/quotation-supplier-cache-entry';
import { TradingMatchSuggestionResponseDto } from '../interfaces/dtos/trading-match-suggestion-response.dto';
import { PurchasingMapperService } from './purchasing-mapper.service';
import { UploadService } from '../../../core/services/api/upload-api.service';

@Injectable({
  providedIn: 'root',
})
export class PurchasingService implements AbstractPurchasingService {
  private readonly urlNodeApi = `${environment.apiUrl}/oferta-estruturada`;
  private readonly _enderecoApi = inject(EnderecoApiService);
  private readonly _uploadService = inject(UploadService);
  private readonly _formTemp = inject(FormTempApiService);
  private readonly _auth = inject(AuthModuleService);
  private readonly _companyContext = inject(CompanyContextService);
  private readonly _tenant = inject(TenantContextService);

  private get empresaId(): number {
    return this._companyContext.companyId() ?? 0;
  }

  /** Papéis que o backend aceita em alterar-comprador / cancelar-oferta. */
  private static readonly PAPEIS_GESTAO_COMPRA: readonly PapelEmpresa[] = [
    PapelEmpresa.GERENTE_COMPRAS,
    PapelEmpresa.ADMIN,
  ];

  /**
   * Capabilities de UI derivadas do **papel do usuário na empresa atual**
   * (`TenantContextService`), espelhando a regra de `verificarPapelEmpresa` do
   * ebarn-node. Reativo: ao trocar de empresa, os botões se ajustam.
   */
  private readonly _capabilities = computed<UiCapability[]>(() => {
    const papel = this._tenant.empresaAtual()?.papel;
    const podeGerir = !!papel && PurchasingService.PAPEIS_GESTAO_COMPRA.includes(papel);
    return podeGerir
      ? [UiCapability.PURCHASING_CHANGE_BUYER, UiCapability.PURCHASING_CANCEL_PURCHASE_REQUEST]
      : [];
  });

  readonly capabilities$: Observable<UiCapability[]> = toObservable(this._capabilities);

  private attachmentGroupsCache = new Map<string, PurchaseRequestAttachmentGroup[]>();
  private readonly router = inject(Router);
  private tempCache = new Map<string, PurchaseRequestResponseDto>();
  private quotationSuppliersCache = new Map<string, Map<string, QuotationSupplierCacheEntry>>();

  constructor(
    private http: HttpClient,
    private mapper: PurchasingMapperService,
  ) {}

  setCache(id: string, data: PurchaseRequestResponseDto): void {
    this.tempCache.set(id, data);
    this.router.events
      .pipe(
        filter(
          (event) =>
            event instanceof NavigationEnd ||
            (event instanceof NavigationCancel && event.code !== NavigationCancellationCode.Redirect) ||
            event instanceof NavigationSkipped ||
            event instanceof NavigationError,
        ),
        take(1),
      )
      .subscribe(() => {
        if (this.tempCache.get(id) === data) this.tempCache.delete(id);
      });
  }

  getCache(id: string): PurchaseRequestResponseDto | undefined {
    return this.tempCache.get(id);
  }

  getAddress(): Observable<PurchaseRequestBranch[]> {
    return this._enderecoApi
      .findEnderecoByIdEmpresa(this.empresaId)
      .pipe(
        map((enderecos) =>
          enderecos.map((endereco) => this.mapper.toPurchaseRequestBranch(endereco)),
        ),
      );
  }

  /** Filiais com o endereço completo (o getAddress acima devolve só id e apelido). */
  getBranchAddresses(): Observable<Endereco[]> {
    return this._enderecoApi.findEnderecoByIdEmpresa(this.empresaId);
  }

  hasCapability(capability: UiCapability): boolean {
    return this._capabilities().includes(capability);
  }

  /**
   * Status da integração ERP da empresa atual — usado pelo kanban para
   * decidir se exibe a coluna "Pendente de Integração" (ver spec
   * `docs/specs/kanban-coluna-integracao-erp-dinamica.md`). O dado já está no
   * cache do `TenantContextService`, sem rota nova.
   *
   * NOTA: este método sobrescreve `getCompanyIntegrationStatus` do
   * `AbstractPurchasingService` de `@aionzdev/modules` — assinatura publicada
   * em paralelo (PR `feat/purchasing-integration-status`). Enquanto a lib não
   * publicar, o método continua válido como método próprio da classe.
   */
  getCompanyIntegrationStatus(): Observable<{ integracaoErpAtiva: boolean }> {
    return of({ integracaoErpAtiva: this._tenant.integracaoErpAtiva() });
  }

  findAllPurchases(
    data?: FindAllPurchasesRequestDto,
  ): Observable<HttpResponse<PurchaseRequestResponseDto[]>> {
    let params = new HttpParams().append('empresaId', this.empresaId);

    data?.filters?.forEach((filter) => {
      if (filter.value != null) {
        params = params.append(filter.input, filter.value);
      }
    });

    if (data?.orderBy) {
      params = params
        .append('orderBy', data.orderBy.key)
        .append('orderDirection', data.orderBy.order);
    }

    return this.http
      .get<FindAllPurchaseOffersResponseDto[]>(`${this.urlNodeApi}`, {
        params,
        observe: 'response',
      })
      .pipe(
        map(
          (apiResponse) =>
            new HttpResponse({
              ...apiResponse,
              url: apiResponse.url!,
              body: apiResponse.body!.map((item) => this.mapper.toPurchaseWithSpreadSheet(item)),
            }),
        ),
      );
  }

  findPurchaseById(purchaseId: string): Observable<HttpResponse<PurchaseRequestResponseDto>> {
    return this.http
      .get<FIndOfferResponseDto>(`${this.urlNodeApi}/oferta-fornecedor`, {
        params: { id: purchaseId },
        observe: 'response',
      })
      .pipe(
        take(1),
        map((apiResponse) => this.mapper.toPurchase(apiResponse)),
        catchError((err) => throwError(() => err)),
      );
  }

  approveQuotationMap(
    data: ApproveQuotationMap,
    config = { hideGlobalError: false },
  ): Observable<HttpResponse<void>> {
    const body: AcceptOffersRequestDto = this.mapper.toAcceptOffer(data);

    return this.http
      .post<void>(`${this.urlNodeApi}/adm-aceita-ofertas`, body, {
        observe: 'response',
        context: config.hideGlobalError
          ? hideGlobalDialogErrorContext
          : showGlobalDialogErrorContext,
      })
      .pipe(
        take(1),
        // Cotação aprovada → o rascunho não vale mais. Best-effort, não bloqueia.
        tap(() =>
          this.deleteQuotationMapDraft(data.purchaseId)
            .pipe(catchError(() => of(void 0)))
            .subscribe(),
        ),
        catchError((err) => throwError(() => err)),
      );
  }

  private chaveRascunhoMapa(purchaseId: string): string {
    return `mapa-cotacao-${purchaseId}`;
  }

  getQuotationMapDraft(purchaseId: string): Observable<QuotationMapDraft | null> {
    return this._auth.userAuthenticatedChange$.pipe(
      take(1),
      switchMap((user) =>
        this._formTemp.getOne<QuotationMapDraft>(
          String(user.id),
          this.chaveRascunhoMapa(purchaseId),
        ),
      ),
    );
  }

  saveQuotationMapDraft(purchaseId: string, draft: QuotationMapDraft): Observable<void> {
    return this._auth.userAuthenticatedChange$.pipe(
      take(1),
      switchMap((user) =>
        this._formTemp.save(String(user.id), this.chaveRascunhoMapa(purchaseId), draft),
      ),
    );
  }

  deleteQuotationMapDraft(purchaseId: string): Observable<void> {
    return this._auth.userAuthenticatedChange$.pipe(
      take(1),
      switchMap((user) =>
        this._formTemp.delete(String(user.id), this.chaveRascunhoMapa(purchaseId)),
      ),
    );
  }

  getQuotationMapBoardData(purchaseId: string): Observable<HttpResponse<QuotationMapData>> {
    const params = { ofertaEstruturadaId: purchaseId };

    const offerRequest$ = this.http
      .get<FIndOfferResponseDto>(`${this.urlNodeApi}/oferta-fornecedor`, {
        params: { id: purchaseId },
        observe: 'response',
      })
      .pipe(
        take(1),
        catchError((err) => throwError(() => err)),
      );

    const offersRequest$ = this.http
      .get<FindAllPurchaseRequestOffersResponseDto>(
        `${this.urlNodeApi}/pedidos-fornecedores-ofertas`,
        {
          params,
          observe: 'response',
        },
      )
      .pipe(
        take(1),
        catchError((err) => throwError(() => err)),
      );

    const suppliersInfo$ = this.getSupplierInfo(purchaseId).pipe(
      take(1),
      catchError(() => throwError(() => new Error('Error fetching supplier info'))),
    );

    return forkJoin({
      offerDetails: offerRequest$,
      offers: offersRequest$,
      suppliersInfo: suppliersInfo$,
    }).pipe(
      map(({ offerDetails, offers, suppliersInfo }) => {
        const suppliersMap = new Map<string, QuotationSupplierCacheEntry>();
        (offerDetails.body?.fornecedoresParticipantes ?? []).forEach((fp) => {
          const supplierId = String(fp.fornecedorId || fp.fornecedor?.id);
          suppliersMap.set(supplierId, {
            nome: fp.fornecedor?.nome || '',
            cnpj: fp.fornecedor?.cpf || '',
            telefone: fp.fornecedor?.telefone || '',
            email: fp.fornecedor?.email || '',
          });
        });
        this.quotationSuppliersCache.set(purchaseId, suppliersMap);

        return this.mapper.toQuotationMap(offerDetails, offers, suppliersInfo);
      }),
    );
  }

  addSupplierToProduct(data: AddSupplierToProductDto): Observable<HttpResponse<void>> {
    const cachedSupplier = this.quotationSuppliersCache.get(data.purchaseId)?.get(data.supplierId);
    const body = this.mapper.toAddSupplierToProductRequestDto(data, cachedSupplier);

    return this.http
      .post<void>(`${this.urlNodeApi}/adicionar-fornecedor-produto`, body, {
        observe: 'response',
      })
      .pipe(
        take(1),
        catchError((err) => throwError(() => err)),
      );
  }

  updatePurchaseRequestStatus(data: UpdatePurchaseRequestStatusDto): Observable<void> {
    const body = {
      id: Number(data.id),
      status: this.mapper.fromSpreadSheetStatus(data.status),
      motivoRecusa: data.reasonDecline,
    };

    return this.http.post<void>(`${this.urlNodeApi}/alterar-status-planilha-pedido`, body).pipe(
      take(1),
      catchError((err) => throwError(() => err)),
    );
  }

  updatePurchaseOrdersStatus(
    _purchaseId: string,
    items: UpdatePurchaseOrderItemDto[],
  ): Observable<void> {
    const statusMap: Record<PurchaseItemStatus, string> = {
      [PurchaseItemStatus.APPROVED]: 'APROVADO',
      [PurchaseItemStatus.DECLINED]: 'REPROVADO',
      [PurchaseItemStatus.REMOVED]: 'REMOVIDO',
      [PurchaseItemStatus.PENDING]: 'PENDENTE',
    };

    const body = {
      itens: items.map((item) => ({
        pedidoId: Number(item.orderId),
        status: statusMap[item.status] ?? 'REPROVADO',
        motivo: item.reason,
      })),
    };

    return this.http.post<void>(`${this.urlNodeApi}/alterar-status-pedido`, body).pipe(
      take(1),
      catchError((err) => throwError(() => err)),
    );
  }

  updatePurchaseItemInfo(orderId: string, data: UpdatePurchaseItemInfoDto): Observable<void> {
    const body = {
      qtd: data.quantity,
      enderecoId: data.branchId ? Number(data.branchId) : undefined,
      atributos: data.attributes,
    };

    return this.http.patch<void>(`${this.urlNodeApi}/comprador-altera-info/${orderId}`, body).pipe(
      take(1),
      catchError((err) => throwError(() => err)),
    );
  }

  startPurchaseRequestQuoting(purchaseId: string): Observable<void> {
    const body = { ofertaId: Number(purchaseId) };
    return this.http.post<void>(`${this.urlNodeApi}/iniciar-cotacao`, body).pipe(
      take(1),
      catchError((err) => throwError(() => err)),
    );
  }

  acceptPurchaseRequestOffers(purchaseId: string): Observable<void> {
    const ofertaEstruturadaId = Number(purchaseId);
    return this.http
      .get<FindAllPurchaseRequestOffersResponseDto>(
        `${this.urlNodeApi}/pedidos-fornecedores-ofertas`,
        {
          params: { ofertaEstruturadaId },
          observe: 'response',
        },
      )
      .pipe(
        take(1),
        switchMap((response) => {
          const ofertasAceitas = (response.body?.ofertas || []).map((oferta) => oferta.id);
          return this.http.post<void>(`${this.urlNodeApi}/setor-compras-aceita-ofertas`, {
            ofertaEstruturadaId,
            ofertasAceitas,
          });
        }),
      )
      .pipe(
        take(1),
        catchError((err) => throwError(() => err)),
      );
  }

  downloadPurchaseFile(purchaseId: string) {
    return this.http.get(`${this.urlNodeApi}/download-planilha-pedidos/?id=${purchaseId}`, {
      responseType: 'blob',
    });
  }

  downloadSupplierQuoteFile(purchaseId: string, supplierId: string): Observable<Blob> {
    const params = new HttpParams()
      .set('structuredOfferId', purchaseId)
      .set('supplierId', supplierId);
    return this.http
      .get(`${this.urlNodeApi}/planilha-fornecedor`, {
        params,
        responseType: 'blob',
      })
      .pipe(
        take(1),
        catchError((err) => throwError(() => err)),
      );
  }

  uploadOfferDocuments(
    data: UploadOfferDocumentsDto,
  ): Observable<PurchaseRequestDocumentUploadItemResult[]> {
    const formData = new FormData();
    formData.append('planilha', data.spreadsheetFile);
    formData.append('pdf', data.supplierQuoteFile);
    formData.append('structuredOfferId', data.purchaseId);
    formData.append('supplierId', data.supplierId);

    return this.http
      .post<void>(`${this.urlNodeApi}/criar-oferta-planilha`, formData, {
        context: hideGlobalDialogErrorContext,
      })
      .pipe(
        take(1),
        map(() => this.mapper.toUploadOfferDocumentsResult()),
        catchError((err) => of(this.mapper.toUploadOfferDocumentsResult(err))),
      );
  }

  findAllProductsForQuote(
    purchaseId: string,
    status?: PurchaseRequestStatus,
  ): Observable<HttpResponse<SendPurchaseRequestForQuoteItem[]>> {
    const apiStatus = this.mapper.toApiOrderQueryStatus(status);
    let params = new HttpParams().set('structuredTradingId', Number(purchaseId));
    if (apiStatus) {
      params = params.set('status', apiStatus);
    }

    return this.http
      .get<FindAllPurchaseProductsResponseDto[]>(`${this.urlNodeApi}/pedidos-admin`, {
        params,
        observe: 'response',
      })
      .pipe(
        map((apiResponse) => {
          const responseBody = apiResponse.body || [];
          const attachmentGroups = this.mapper.toPurchaseRequestAttachmentGroups(responseBody);
          if (attachmentGroups.length) {
            this.attachmentGroupsCache.set(purchaseId, attachmentGroups);
          }

          return new HttpResponse<SendPurchaseRequestForQuoteItem[]>({
            ...apiResponse,
            url: apiResponse.url ?? undefined,
            body: responseBody.map((dto) => this.mapper.toItemForQuote(dto)),
          });
        }),
      );
  }

  findPurchaseRequestAttachments(
    purchaseId: string,
    status?: PurchaseRequestStatus,
  ): Observable<HttpResponse<PurchaseRequestAttachmentGroup[]>> {
    const cachedGroups = this.attachmentGroupsCache.get(purchaseId);
    if (cachedGroups) {
      return of(
        new HttpResponse<PurchaseRequestAttachmentGroup[]>({
          body: cachedGroups,
        }),
      );
    }

    return this.findAllProductsForQuote(purchaseId, status).pipe(
      map((response) => {
        const groups = this.attachmentGroupsCache.get(purchaseId) ?? [];
        return new HttpResponse<PurchaseRequestAttachmentGroup[]>({
          ...response,
          url: response.url ?? undefined,
          body: groups,
        });
      }),
    );
  }

  downloadPurchaseRequestAttachment(purchaseId: string, attachmentId: string): Observable<Blob> {
    const groups = this.attachmentGroupsCache.get(purchaseId) || [];
    const allAttachments = groups.flatMap((group) => group.attachments);
    const attachment = allAttachments.find((item) => item.id === attachmentId);

    if (!attachment || !attachment.url) {
      return throwError(() => new Error('Attachment not found in cache or missing download URL'));
    }

    return this.http
      .get(attachment.url, {
        responseType: 'blob',
      })
      .pipe(
        take(1),
        catchError((err) => throwError(() => err)),
      );
  }

  getCustomerTradingFocs(purchaseId: string): Observable<CustomerTradingFocItem[]> {
    const params = new HttpParams().append('structuredOfferId', purchaseId);

    return this.http
      .get<CustomerTradingFocResponseDto[]>(`${this.urlNodeApi}/admin-focs-signatures`, { params })
      .pipe(
        map((apiResponse) => apiResponse.map((item) => this.mapper.toCustomerTradingFocItem(item))),
        take(1),
        catchError((err) => throwError(() => err)),
      );
  }

  createSupplier(supplier: PurchaseSupplierCreate) {
    const body = this.mapper.toNewSupplierRequestDto(supplier);
    return this.http
      .post<any>(`${this.urlNodeApi}/adicionar-fornecedor`, body, {
        observe: 'response',
      })
      .pipe(
        map((res) => {
          return res.clone({
            body: {
              ...supplier,
              id: res.body.fornecedorId,
            },
          });
        }),
        take(1),
        catchError((err) => throwError(() => err)),
      );
  }

  createSupplierForProduct(supplier: PurchaseSupplierCreate, config: { hideGlobalError?: boolean } = {}) {
    const body = this.mapper.toNewSupplierRequestDto(supplier);
    return this.http
      .post<any>(`${this.urlNodeApi}/adicionar-fornecedor-produto`, body, {
        observe: 'response',
        context: config.hideGlobalError ? hideGlobalDialogErrorContext : showGlobalDialogErrorContext,
      })
      .pipe(
        map((res) => {
          return res.clone({
            body: {
              ...supplier,
              id: res.body.fornecedorId,
            },
          });
        }),
        take(1),
        catchError((err) => throwError(() => err)),
      );
  }

  deleteSupplier(supplierId: string, purchaseId?: string) {
    let params = new HttpParams();
    if (purchaseId) {
      params = params
        .set('ofertaEstruturadaId', Number(purchaseId))
        .set('fornecedorId', Number(supplierId));
    } else {
      params = params.set('id', Number(supplierId));
    }

    return this.http
      .delete<void>(`${this.urlNodeApi}/remover-fornecedor`, { params, observe: 'response' })
      .pipe(
        take(1),
        catchError((err) => throwError(() => err)),
      );
  }

  deleteSupplierProducts(supplierId: string, productId: string) {
    const params = new HttpParams()
      .set('fornecedorId', Number(supplierId))
      .set('produtoId', Number(productId));
    return this.http.delete<void>(`${this.urlNodeApi}/remover-fornecedor-produto`, {
      params,
      observe: 'response',
    });
  }

  checkForDuplicatedProducts(
    purchaseId: string,
  ): Observable<HttpResponse<DuplicatedProductResponseDto[]>> {
    return this.http
      .get<CheckDuplicatedProductsResponseDto[]>(`${this.urlNodeApi}/pedidos-duplicados`, {
        params: { ofertaId: Number(purchaseId) },
        observe: 'response',
      })
      .pipe(
        map((apiResponse) => {
          return new HttpResponse({
            ...apiResponse,
            url: apiResponse.url!,
            body: apiResponse.body!.map((item) => this.mapper.maptoDuplicatedProductResponse(item)),
          });
        }),
        take(1),
        catchError((err) => throwError(() => err)),
      );
  }

  mergePurchaseRequestItems(data: MergePurchaseRequestItemsDto): Observable<boolean> {
    const body = this.mapper.toMergePurchasesRequestDto(data);
    return this.http.post<void>(`${this.urlNodeApi}/unificar-pedidos`, body).pipe(
      map(() => true),
      take(1),
      catchError((err) => throwError(() => err)),
    );
  }

  getSupplierInfo(purchaseId: string): Observable<SupplierInfo[]> {
    return this.http
      .get<SupplierInfoResponseDto[]>(
        `${this.urlNodeApi}/fornecedores?ofertaEstruturadaId=${purchaseId}`,
      )
      .pipe(
        map((apiResponse) => apiResponse.map(this.mapper.toSupplierInfo)),
        take(1),
        catchError((err) => throwError(() => err)),
      );
  }

  resendQuotationRequest(supplierId: string, purchaseId: string): Observable<boolean> {
    const body = {
      ofertaId: Number(purchaseId),
      fornecedorId: Number(supplierId),
    };
    return this.http.post<void>(`${this.urlNodeApi}/reenviar-email-cotacao`, body).pipe(
      map(() => true),
      take(1),
      catchError((err) => throwError(() => err)),
    );
  }

  /**
   * Cotações aguardando aprovação de cada item (id do item → ids das cotações vencedoras do mapa).
   * Vem da mesma rota do diálogo de integração, que devolve os itens com as cotações escolhidas.
   */
  findApprovalWinners(purchaseId: string): Observable<Record<string, string[]>> {
    return this.http
      .get<UncompletedPurchaseResponseDto>(`${this.urlNodeApi}/pedidos-nao-completado`, {
        params: { ofertaEstruturadaId: purchaseId },
      })
      .pipe(
        take(1),
        map((response) => {
          const winners: Record<string, string[]> = {};
          for (const pedido of response?.pedidos ?? []) {
            const pending = (pedido.pedidoTradings ?? [])
              .filter((trading) => trading.status === 'aprovacao_pendente')
              .map((trading) => String(trading.tradingId));
            if (pending.length) winners[String(pedido.id)] = pending;
          }
          return winners;
        }),
      );
  }

  findUncompletedPurchases(
    purchaseId: string,
  ): Observable<HttpResponse<ProductIntegrationDialogData>> {
    return this.http
      .get<UncompletedPurchaseResponseDto>(`${this.urlNodeApi}/pedidos-nao-completado`, {
        params: { ofertaEstruturadaId: purchaseId },
        observe: 'response',
      })
      .pipe(
        take(1),
        map((apiResponse) => {
          return new HttpResponse({
            ...apiResponse,
            url: apiResponse.url!,
            body: this.mapper.toUncompletedPurchaseDialogData(apiResponse.body!),
          });
        }),
        catchError((err) => throwError(() => err)),
      );
  }

  getStageTimeMetrics() {
    const params = new HttpParams().set('empresaId', this.empresaId);
    return this.http
      .get<StageTimeMetricsResponseDto>(`${this.urlNodeApi}/metricas/tempo-por-etapa`, { params })
      .pipe(
        take(1),
        map((response) => ({
          pendente: response.pendente ?? undefined,
          aceita: response.aceita ?? undefined,
          em_cotacao: response.em_cotacao ?? undefined,
          aguardando_aprovacao: response.aprovacao_pendente ?? undefined,
          pendente_integracao: response.nao_completada ?? undefined,
        })),
        catchError((err) => throwError(() => err)),
      );
  }
  getAverageSlaLabels(filters?: Filter[]): Observable<TimePerStageMetrics> {
    let params = new HttpParams().append('empresaId', this.empresaId);

    filters?.forEach((filter) => {
      if (filter.value != null) {
        params = params.append(filter.input, filter.value);
      }
    });

    return this.http
      .get<StageTimeMetricsResponseDto>(`${this.urlNodeApi}/metricas/tempo-por-etapa`, { params })
      .pipe(
        take(1),
        map((response) => this.mapper.toAverageSlaLabels(response)),
        catchError((err) => throwError(() => err)),
      );
  }

  findBuyersByGroupAndUserName(dto: {
    search: string;
    currentPage: number;
    take: number;
  }): Observable<SelectorListResponse<BuyerItem>> {
    const params = new HttpParams()
      .set('groupName', 'Conceito')
      .set('userName', dto.search)
      .set('page', dto.currentPage.toString())
      .set('limit', dto.take.toString());

    return this.http
      .get<BuyerResponseDto[]>(
        `${environment.apiUrl}/grupo-usuarios/filter-by-group-and-user-name`,
        {
          params,
          observe: 'response',
        },
      )
      .pipe(
        map((res) => {
          const totalCount = Number(res.headers.get('X-Total-Count') || 0);
          const pagesCount = Math.ceil(totalCount / dto.take);
          return {
            pagesCount: pagesCount || 1,
            items: (res.body || []).map((item) => this.mapper.toBuyer(item)),
          };
        }),
        take(1),
        catchError((err) => throwError(() => err)),
      );
  }

  getRetratoAtual(): Observable<HttpResponse<RetratoAtualResponseDto>> {
    const params = new HttpParams().set('empresaId', this.empresaId);
    return this.http
      .get<RetratoAtualResponseDto>(`${this.urlNodeApi}/metricas/retrato-atual`, {
        params,
        observe: 'response',
      })
      .pipe(
        take(1),
        catchError((err) => throwError(() => err)),
      );
  }

  getDashboardMetrics(
    dataInicio: string,
    dataFim: string,
  ): Observable<HttpResponse<DashboardMetricsResponseDto>> {
    const params = new HttpParams()
      .set('empresaId', this.empresaId)
      .set('dataInicio', dataInicio)
      .set('dataFim', dataFim);
    return this.http
      .get<DashboardMetricsResponseDto>(`${this.urlNodeApi}/metricas/dashboard`, {
        params,
        observe: 'response',
      })
      .pipe(
        take(1),
        catchError((err) => throwError(() => err)),
      );
  }

  updateBuyer(purchaseId: string, buyerId: string): Observable<void> {
    const body = {
      ofertaEstruturadaId: Number(purchaseId),
      approverId: Number(buyerId),
    };

    return this.http.post<void>(`${this.urlNodeApi}/alterar-comprador-oferta`, body).pipe(
      take(1),
      catchError((err) => throwError(() => err)),
    );
  }

  cancelPurchaseRequest(purchaseId: string, reason: string): Observable<void> {
    const boy = {
      structureOfferId: Number(purchaseId),
      motivoCancelamento: reason,
    };
    return this.http.post<void>(`${this.urlNodeApi}/cancelar-oferta`, boy).pipe(
      take(1),
      catchError((err) => throwError(() => err)),
    );
  }

  getRealtimeConfig(): RealtimeConfig {
    return {
      tenantId: this.empresaId,
      eventName: 'oferta-estruturada:atualizada',
      joinEvent: 'joinOfertaCanal',
      leaveEvent: 'leaveOfertaCanal',
      updateTypes: {
        board: [
          'new_offer',
          'status_changed',
          'supplier_responded',
          'buyer_changed',
          'info_changed',
          'item_status_changed',
        ],
        pendingDetail: [
          'status_changed',
          'item_status_changed',
          'info_changed',
          'buyer_changed',
          'comment_created',
        ],
        purchaseDetail: [
          'status_changed',
          'item_status_changed',
          'info_changed',
          'buyer_changed',
          'comment_created',
          'fornecedor_sugerido',
        ],
        quotationMap: [
          'supplier_responded',
          'status_changed',
          'info_changed',
          'buyer_changed',
          'item_status_changed',
          'fornecedor_sugerido',
        ],
        activity: [
          'status_changed',
          'info_changed',
          'buyer_changed',
          'supplier_responded',
          'comment_created',
          'item_status_changed',
        ],
        integration: ['status_changed', 'info_changed', 'item_status_changed'],
      },
    };
  }

  getPurchasingEvent(event: any): PurchasingEvent {
    return this.mapper.toPurchasingEvent(event);
  }

  getPurchaseRequestAudits(
    data: PurchaseRequestAuditRequestDto,
  ): Observable<HttpResponse<PurchaseRequestAuditDto[]>> {
    let params = new HttpParams().append('structuredOfferId', data.purchaseId);

    if (data.page !== undefined) {
      params = params.append('page', data.page.toString());
    }
    if (data.pageSize !== undefined) {
      params = params.append('pageSize', data.pageSize.toString());
    }

    return this.http
      .get<PurchaseRequestAuditResponseDto[]>(`${this.urlNodeApi}/audits`, {
        params,
        observe: 'response',
      })
      .pipe(
        map((apiResponse) => {
          return new HttpResponse({
            ...apiResponse,
            url: apiResponse.url || undefined,
            body: (apiResponse.body || []).map((item) => this.mapper.toPurchaseRequestAudit(item)),
          });
        }),
        catchError((err) => throwError(() => err)),
      );
  }
  getPurchaseRequestComments(
    purchaseId: string,
    config: { hideGlobalError?: boolean } = {},
  ): Observable<HttpResponse<PurchaseRequestCommentDto[]>> {
    return this.http
      .get<PurchaseRequestCommentResponseDto[]>(`${this.urlNodeApi}/${purchaseId}/comments`, {
        observe: 'response',
        context: config.hideGlobalError
          ? hideGlobalDialogErrorContext
          : showGlobalDialogErrorContext,
      })
      .pipe(map((response) => this.mapper.toPurchaseRequestCommentsResponse(response, purchaseId)));
  }

  createPurchaseRequestComment(
    purchaseId: string,
    text: string,
    fileUrl?: string,
  ): Observable<HttpResponse<PurchaseRequestCommentDto>> {
    const body = {
      structuredOfferId: Number(purchaseId),
      content: text,
      attachment: fileUrl,
    };

    return this.http
      .post<PurchaseRequestCommentResponseDto>(`${this.urlNodeApi}/comments`, body, {
        observe: 'response',
      })
      .pipe(
        map((response) => this.mapper.toPurchaseRequestCommentResponse(response, purchaseId, text)),
      );
  }

  sendPurchaseRequestCommentFile(purchaseId: string, file: File): Observable<HttpResponse<string>> {
    return this._uploadService.single(`purchase-request-comment/${purchaseId}`, file).pipe(
      map((response) => {
        return new HttpResponse({
          url: response.location || undefined,
          body: response.location || undefined,
        });
      }),
    );
  }

  updateSuggestedSupplierStatus(
    data: UpdateSuggestedSupplierStatusDto,
  ): Observable<HttpResponse<void>> {
    const body = {
      ofertaEstruturadaId: Number(data.purchaseId),
      produtoId: Number(data.productId),
      fornecedorId: Number(data.supplierId),
      status: data.status,
    };

    return this.http
      .post<void>(`${this.urlNodeApi}/alterar-status-fornecedor-sugerido`, body, {
        observe: 'response',
      })
      .pipe(
        take(1),
        catchError((err) => throwError(() => err)),
      );
  }

  triggerSupplierSuggestionSearch(
    purchaseId: string,
  ): Observable<HttpResponse<TriggerSupplierSuggestionSearchResult>> {
    return this.http
      .post<TriggerSupplierSuggestionSearchResult>(
        `${this.urlNodeApi}/${purchaseId}/buscar-fornecedores-sugeridos`,
        {},
        { observe: 'response' },
      )
      .pipe(
        take(1),
        catchError((err) => throwError(() => err)),
      );
  }

  getTradingMatchSuggestions(
    purchaseId: string,
  ): Observable<HttpResponse<TradingMatchSuggestion[]>> {
    return this.http
      .get<TradingMatchSuggestionResponseDto[]>(
        `${this.urlNodeApi}/${purchaseId}/sugestoes-trading-parceiro`,
        { observe: 'response', context: hideGlobalDialogErrorContext },
      )
      .pipe(
        take(1),
        map((apiResponse) => {
          return new HttpResponse({
            ...apiResponse,
            url: apiResponse.url ?? undefined,
            body: (apiResponse.body ?? []).map((item) =>
              this.mapper.toTradingMatchSuggestion(item),
            ),
          });
        }),
        catchError((err) => throwError(() => err)),
      );
  }

  updateTradingMatchSuggestionStatus(
    data: UpdateTradingMatchSuggestionStatusDto,
  ): Observable<HttpResponse<void>> {
    const body = { status: data.status };

    return this.http
      .put<void>(
        `${this.urlNodeApi}/${data.purchaseId}/sugestoes-trading-parceiro/${data.matchId}`,
        body,
        { observe: 'response' },
      )
      .pipe(
        take(1),
        catchError((err) => throwError(() => err)),
      );
  }
}
